import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  OFFLINE_BALL_TRAJECTORIES,
  OFFLINE_GAME_INITIAL_STATS,
  OFFLINE_GAME_STATE,
  OFFLINE_GAME_TIME_MS,
  OFFLINE_GOAL_MESSAGES,
  OFFLINE_POWER_BAR_COLORS,
  OFFLINE_POWER_RULES,
  OFFLINE_RESULT_ICONS,
  OFFLINE_SAVED_MESSAGES,
  OFFLINE_SHOOT_ZONES,
  OFFLINE_STREAK,
  OFFLINE_ZONE_ADJACENCY,
  OFFLINE_ZONE_LABELS,
  type OfflineGameState,
  type OfflineShootZone,
} from '../../../core/constants/constants';
import { OFFLINE_OVERLAY_ANIMATIONS } from '../../../core/constants/animations';
import { NetworkService } from '../../../core/services/ui/network.service';
import { GameStats } from '../../../core/interfaces/ui/games-stats.interface';

@Component({
  selector: 'app-offline-overlay',
  templateUrl: './offline-overlay.component.html',
  styleUrls: ['./offline-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [...OFFLINE_OVERLAY_ANIMATIONS],
})
export class OfflineOverlayComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private previousOfflineState: boolean | null = null;

  readonly GAME_STATE = OFFLINE_GAME_STATE;
  readonly STREAK = OFFLINE_STREAK;
  readonly shootZones = OFFLINE_SHOOT_ZONES;
  readonly zoneLabels = OFFLINE_ZONE_LABELS;

  isOffline = false;
  showGame = false;
  gameState: OfflineGameState = OFFLINE_GAME_STATE.IDLE;
  selectedZone: OfflineShootZone | null = null;
  goalkeeperZone: OfflineShootZone | null = null;
  ballStyle: Record<string, string> = {};
  reconnecting = false;

  stats: GameStats = { ...OFFLINE_GAME_INITIAL_STATS };

  powerBarActive = false;
  powerLevel: number = OFFLINE_POWER_RULES.MIN;
  private powerInterval: ReturnType<typeof setInterval> | null = null;
  private powerDirection: number = OFFLINE_POWER_RULES.ASCENDING_DIRECTION;

  countdownValue: number = OFFLINE_POWER_RULES.MIN;

  resultMessage = '';
  resultIcon = '';

  constructor(
    private networkService: NetworkService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.networkService.isOffline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((offline) => {
        const wasOffline = this.previousOfflineState;
        this.previousOfflineState = offline;
        this.isOffline = offline;

        if (offline) {
          this.resetGame();
          this.showGame = false;
          this.reconnecting = false;
        } else if (wasOffline === true) {
          this.reconnecting = true;
          setTimeout(() => {
            this.reconnecting = false;
            this.cdr.markForCheck();
          }, OFFLINE_GAME_TIME_MS.RECONNECT_TOAST);
        }

        this.cdr.markForCheck();
      });
  }

  startGame(): void {
    this.showGame = true;
    this.resetGame();
    this.cdr.markForCheck();
  }

  resetGame(): void {
    this.gameState = OFFLINE_GAME_STATE.IDLE;
    this.selectedZone = null;
    this.goalkeeperZone = null;
    this.powerBarActive = false;
    this.powerLevel = OFFLINE_POWER_RULES.MIN;
    this.resultMessage = '';
    this.resultIcon = '';
    this.countdownValue = OFFLINE_POWER_RULES.MIN;
    this.clearPowerInterval();
  }

  selectZone(zone: OfflineShootZone): void {
    if (this.gameState !== OFFLINE_GAME_STATE.IDLE) return;

    this.selectedZone = zone;
    this.gameState = OFFLINE_GAME_STATE.AIMING;
    this.startPowerBar();
    this.cdr.markForCheck();
  }

  @HostListener('window:keydown.space', ['$event'])
  onSpacePress(event: KeyboardEvent): void {
    const isGameReadyForSpaceShot =
      this.isOffline &&
      this.showGame &&
      this.gameState === OFFLINE_GAME_STATE.AIMING;

    if (!isGameReadyForSpaceShot) {
      return;
    }

    event.preventDefault();
    this.confirmShot();
  }

  confirmShot(): void {
    if (
      this.gameState !== OFFLINE_GAME_STATE.AIMING ||
      !this.powerBarActive ||
      !this.selectedZone
    ) {
      return;
    }

    this.clearPowerInterval();
    this.powerBarActive = false;
    this.gameState = OFFLINE_GAME_STATE.SHOOTING;

    this.goalkeeperZone = this.getRandomGoalkeeperZone();

    const isGoal = this.calculateResult();

    this.ballStyle = this.getBallTrajectory(this.selectedZone);

    this.cdr.markForCheck();

    setTimeout(() => {
      if (isGoal) {
        this.gameState = OFFLINE_GAME_STATE.GOAL;
        this.stats.goals++;
        this.stats.streak++;

        if (this.stats.streak > this.stats.bestStreak) {
          this.stats.bestStreak = this.stats.streak;
        }

        this.resultMessage = this.getGoalMessage();
        this.resultIcon = OFFLINE_RESULT_ICONS.GOAL;
      } else {
        this.gameState = OFFLINE_GAME_STATE.SAVED;
        this.stats.saves++;
        this.stats.streak = OFFLINE_POWER_RULES.MIN;
        this.resultMessage = this.getSavedMessage();
        this.resultIcon = OFFLINE_RESULT_ICONS.SAVED;
      }

      this.stats.shots++;
      this.cdr.markForCheck();

      setTimeout(() => {
        if (this.isOffline && this.showGame) {
          this.gameState = OFFLINE_GAME_STATE.IDLE;
          this.selectedZone = null;
          this.goalkeeperZone = null;
          this.powerLevel = OFFLINE_POWER_RULES.MIN;
          this.resultMessage = '';
          this.resultIcon = '';
          this.cdr.markForCheck();
        }
      }, OFFLINE_GAME_TIME_MS.NEXT_ROUND_DELAY);
    }, OFFLINE_GAME_TIME_MS.SHOT_RESOLUTION);
  }

  private startPowerBar(): void {
    this.powerBarActive = true;
    this.powerLevel = OFFLINE_POWER_RULES.MIN;
    this.powerDirection = OFFLINE_POWER_RULES.ASCENDING_DIRECTION;

    this.powerInterval = setInterval(() => {
      this.powerLevel += this.powerDirection * OFFLINE_POWER_RULES.STEP;

      if (this.powerLevel >= OFFLINE_POWER_RULES.MAX) {
        this.powerLevel = OFFLINE_POWER_RULES.MAX;
        this.powerDirection = OFFLINE_POWER_RULES.DESCENDING_DIRECTION;
      } else if (this.powerLevel <= OFFLINE_POWER_RULES.MIN) {
        this.powerLevel = OFFLINE_POWER_RULES.MIN;
        this.powerDirection = OFFLINE_POWER_RULES.ASCENDING_DIRECTION;
      }

      this.cdr.markForCheck();
    }, OFFLINE_GAME_TIME_MS.POWER_TICK);
  }

  private clearPowerInterval(): void {
    if (this.powerInterval) {
      clearInterval(this.powerInterval);
      this.powerInterval = null;
    }
  }

  private getRandomGoalkeeperZone(): OfflineShootZone {
    return this.shootZones[Math.floor(Math.random() * this.shootZones.length)];
  }

  private calculateResult(): boolean {
    if (!this.selectedZone || !this.goalkeeperZone) {
      return false;
    }

    const sameZone = this.selectedZone === this.goalkeeperZone;
    const powerAccuracy = this.getPowerAccuracy();

    if (sameZone) {
      return (
        Math.random() >
        OFFLINE_POWER_RULES.SAME_ZONE_BASE -
          powerAccuracy * OFFLINE_POWER_RULES.SAME_ZONE_ACCURACY_BONUS
      );
    }

    const isAdjacent = this.isAdjacentZone(
      this.selectedZone,
      this.goalkeeperZone,
    );

    if (isAdjacent) {
      return (
        Math.random() >
        OFFLINE_POWER_RULES.ADJACENT_ZONE_BASE -
          powerAccuracy * OFFLINE_POWER_RULES.ADJACENT_ZONE_ACCURACY_BONUS
      );
    }

    const isExtremePower =
      this.powerLevel > OFFLINE_POWER_RULES.EXTREME_HIGH ||
      this.powerLevel < OFFLINE_POWER_RULES.EXTREME_LOW;

    if (isExtremePower) {
      return Math.random() > OFFLINE_POWER_RULES.EXTREME_SHOT_BASE;
    }

    return true;
  }

  private getPowerAccuracy(): number {
    if (
      this.powerLevel >= OFFLINE_POWER_RULES.PERFECT_MIN &&
      this.powerLevel <= OFFLINE_POWER_RULES.PERFECT_MAX
    ) {
      return OFFLINE_POWER_RULES.PERFECT_ACCURACY;
    }

    if (
      this.powerLevel >= OFFLINE_POWER_RULES.GOOD_MIN &&
      this.powerLevel <= OFFLINE_POWER_RULES.GOOD_MAX
    ) {
      return OFFLINE_POWER_RULES.GOOD_ACCURACY;
    }

    if (
      this.powerLevel >= OFFLINE_POWER_RULES.OK_MIN &&
      this.powerLevel <= OFFLINE_POWER_RULES.OK_MAX
    ) {
      return OFFLINE_POWER_RULES.OK_ACCURACY;
    }

    return OFFLINE_POWER_RULES.LOW_ACCURACY;
  }

  private isAdjacentZone(
    zone1: OfflineShootZone,
    zone2: OfflineShootZone,
  ): boolean {
    return OFFLINE_ZONE_ADJACENCY[zone1]?.includes(zone2) ?? false;
  }

  private getBallTrajectory(zone: OfflineShootZone): Record<string, string> {
    const trajectory = OFFLINE_BALL_TRAJECTORIES[zone];

    return {
      '--ball-end-x': trajectory.x,
      '--ball-end-y': trajectory.y,
    };
  }

  private getGoalMessage(): string {
    if (this.stats.streak >= OFFLINE_STREAK.SPECIAL_MESSAGE_MIN) {
      return `¡${this.stats.streak} ${OFFLINE_STREAK.SPECIAL_MESSAGE_SUFFIX}`;
    }

    return this.getRandomMessage(OFFLINE_GOAL_MESSAGES);
  }

  private getSavedMessage(): string {
    return this.getRandomMessage(OFFLINE_SAVED_MESSAGES);
  }

  private getRandomMessage(messages: readonly string[]): string {
    return messages[Math.floor(Math.random() * messages.length)];
  }

  get accuracyPercentage(): number {
    if (this.stats.shots === OFFLINE_POWER_RULES.MIN) {
      return OFFLINE_POWER_RULES.MIN;
    }

    return Math.round((this.stats.goals / this.stats.shots) * 100);
  }

  get powerBarColor(): string {
    if (
      this.powerLevel >= OFFLINE_POWER_RULES.PERFECT_MIN &&
      this.powerLevel <= OFFLINE_POWER_RULES.PERFECT_MAX
    ) {
      return OFFLINE_POWER_BAR_COLORS.HIGH_ACCURACY;
    }

    if (
      this.powerLevel >= OFFLINE_POWER_RULES.GOOD_MIN &&
      this.powerLevel <= OFFLINE_POWER_RULES.GOOD_MAX
    ) {
      return OFFLINE_POWER_BAR_COLORS.MEDIUM_ACCURACY;
    }

    return OFFLINE_POWER_BAR_COLORS.LOW_ACCURACY;
  }

  trackByZone(_index: number, zone: OfflineShootZone): string {
    return zone;
  }

  ngOnDestroy(): void {
    this.clearPowerInterval();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
